import clsx from 'clsx'
import { Link } from 'react-router-dom'
import { TeamAvatar } from '@/components/ui/TeamAvatar'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { RankChip } from '@/components/ui/RankChip'

interface GameData {
  id: string
  starts_at: string | null
  status: string
  period?: string | null
  clock?: string | null
  home_team_name: string
  away_team_name: string
  home_team_slug?: string | null
  away_team_slug?: string | null
  home_score: number | null
  away_score: number | null
  venue: string | null
  recap_url?: string | null
  boxscore_url?: string | null
  home_team_conference?: string
  away_team_conference?: string
  home_team_logo_url?: string | null
  away_team_logo_url?: string | null
  home_team_rank?: number | null
  away_team_rank?: number | null
}

interface ScoreCardProps {
  game: GameData
  sport: string
}

export function ScoreCard({ game, sport }: ScoreCardProps) {
  const statusLower = game.status?.toLowerCase()
  const isFinal = statusLower === 'final'
  const isLive = statusLower === 'live' || statusLower === 'in_progress'
  const homeWon = isFinal && game.home_score != null && game.away_score != null && game.home_score > game.away_score
  const awayWon = isFinal && game.home_score != null && game.away_score != null && game.away_score > game.home_score

  return (
    <div className="score-card animate-fade-in">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-slate-500">
          {game.starts_at ? (() => {
            const d = new Date(game.starts_at)
            if (isNaN(d.getTime())) return ''
            const date = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
            const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
            return `${date} · ${time}`
          })() : ''}
        </span>
        <div className="flex items-center gap-2">
          {isLive && (game.period || game.clock) && (
            <span className="text-xs font-mono text-red-400">
              {[game.period, game.clock].filter(Boolean).join(' · ')}
            </span>
          )}
          <StatusBadge status={game.status} />
        </div>
      </div>

      <TeamRow
        name={game.away_team_name}
        slug={game.away_team_slug}
        conference={game.away_team_conference}
        logoUrl={game.away_team_logo_url}
        rank={game.away_team_rank}
        score={game.away_score}
        isWinner={awayWon}
        sport={sport}
      />
      <div className="border-t border-pitch-700/30 my-1" />
      <TeamRow
        name={game.home_team_name}
        slug={game.home_team_slug}
        conference={game.home_team_conference}
        logoUrl={game.home_team_logo_url}
        rank={game.home_team_rank}
        score={game.home_score}
        isWinner={homeWon}
        sport={sport}
      />

      <div className="mt-3 flex items-center justify-between gap-2">
        {game.venue && (
          <span className="text-xs text-slate-600 truncate">{game.venue}</span>
        )}
        {(game.recap_url || game.boxscore_url) && (
          <a
            href={(game.recap_url || game.boxscore_url) as string}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 text-xs text-accent hover:underline"
          >
            {game.recap_url ? 'Recap' : 'Box score'} →
          </a>
        )}
      </div>
    </div>
  )
}

function TeamRow({ name, slug, conference, logoUrl, rank, score, isWinner, sport }: {
  name: string
  slug?: string | null
  conference?: string
  logoUrl?: string | null
  rank?: number | null
  score: number | null
  isWinner: boolean
  sport: string
}) {
  const nameClass = clsx(
    'text-sm truncate transition-colors',
    isWinner ? 'font-bold text-white' : 'text-slate-300',
  )
  return (
    <div className="flex items-center justify-between py-1.5">
      <div className="flex items-center gap-2.5 min-w-0">
        <TeamAvatar name={name || '?'} logoUrl={logoUrl} colorSeed={conference} size="sm" />
        <RankChip rank={rank} />
        {slug ? (
          <Link to={`/${sport}/teams/${slug}`} className={clsx(nameClass, 'hover:text-accent')}>
            {name}
          </Link>
        ) : (
          <span className={nameClass}>{name}</span>
        )}
      </div>
      <span className={clsx(
        'text-lg font-mono ml-3',
        isWinner ? 'font-bold text-white' : 'text-slate-400',
      )} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {score ?? '-'}
      </span>
    </div>
  )
}
